import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { purchasesAPI, getRawMaterials } from '../utils/api';
import { Container, Row, Col, Card, Table, Alert, Button, Spinner, Form } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PurchaseExecutiveSummary = () => {
    const { officerName } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [vendorFilter, setVendorFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [showPayments, setShowPayments] = useState(true);
    const [showOpeningBalance, setShowOpeningBalance] = useState(true);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [stats, setStats] = useState({
        totalPurchases: 0,
        grossPurchases: 0,
        monthlyPurchases: 0,
        weeklyPurchases: 0,
        totalPayments: 0,
        transactionCount: 0
    });

    useEffect(() => {
        fetchPurchaseData();
        fetchRawMaterials();
    }, [officerName]);

    const fetchRawMaterials = async () => {
        try {
            const data = await getRawMaterials();
            setRawMaterials(data || []);
        } catch (err) {
            console.error('Error fetching raw materials:', err);
        }
    };

    useEffect(() => {
        const filtered = transactions.filter(t => {
            // Date Filter
            const tDate = new Date(t.transaction_date);
            tDate.setHours(0, 0, 0, 0);

            let start = fromDate ? new Date(fromDate) : null;
            if (start) start.setHours(0, 0, 0, 0);

            let end = toDate ? new Date(toDate) : null;
            if (end) end.setHours(0, 0, 0, 0);

            if (start && tDate < start) return false;
            if (end && tDate > end) return false;

            // Vendor Filter
            if (vendorFilter && !t.client_name?.toLowerCase().includes(vendorFilter.toLowerCase())) {
                return false;
            }

            // Product Filter
            if (productFilter) {
                const searchLower = productFilter.toLowerCase();
                const matchesParent = t.product?.toLowerCase().includes(searchLower);
                const matchesItems = t.items?.some(item => item.product?.toLowerCase().includes(searchLower));

                if (!matchesParent && !matchesItems) {
                    return false;
                }
            }

            // Payment/Opening Balance Toggles
            const type = (t.transaction_type || '').toLowerCase();
            if (!showPayments && type === 'payment') return false;
            if (!showOpeningBalance && (type === 'opening_balance' || t.product === 'Opening Balance')) return false;

            return true;
        });
        setFilteredTransactions(filtered);
        calculateStats(filtered);
    }, [transactions, fromDate, toDate, vendorFilter, productFilter, showPayments, showOpeningBalance]);

    const fetchPurchaseData = async () => {
        try {
            setLoading(true);
            const response = await purchasesAPI.getPurchasesByOfficer(officerName);
            const data = response.data || [];

            setTransactions(data);
            setFilteredTransactions(data);
            calculateStats(data);
        } catch (err) {
            // console.error('Error fetching data:', err);
            setError('Failed to load purchase data.');
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (data) => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const startOfWeek = new Date(now);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);

        let total = 0;
        let gross = 0;
        let monthly = 0;
        let weekly = 0;
        let payments = 0;

        data.forEach(t => {
            const type = (t.transaction_type || '').toLowerCase();
            const amtPaid = parseFloat(t.amount_paid || 0);

            if (!isNaN(amtPaid)) {
                payments += amtPaid;
            }

            // Calculate value based on items if product filter is active
            let val = 0;
            const searchLower = productFilter?.toLowerCase();

            if (searchLower) {
                if (t.items && t.items.length > 0) {
                    t.items.forEach(item => {
                        if (item.product?.toLowerCase().includes(searchLower)) {
                            const itemAmt = parseFloat(item.total_amount || 0);
                            if (type === 'purchase') {
                                val += itemAmt;
                            } else if (type === 'return' || type === 'debit_note') {
                                val -= itemAmt;
                            }
                        }
                    });
                } else if (t.product?.toLowerCase().includes(searchLower)) {
                    const q = parseFloat(t.quantity) || 0;
                    const p = parseFloat(t.unit_price) || parseFloat(t.price) || 0;
                    const amount = parseFloat(t.total_amount) || (q * p);
                    if (!isNaN(amount)) {
                        if (type === 'purchase') {
                            val = amount;
                        } else if (type === 'return' || type === 'debit_note') {
                            val = -amount;
                        }
                    }
                }
            } else {
                const q = parseFloat(t.quantity) || 0;
                const p = parseFloat(t.unit_price) || parseFloat(t.price) || 0;
                const amount = parseFloat(t.total_amount) || (q * p);
                if (!isNaN(amount)) {
                    if (type === 'purchase') {
                        val = amount;
                    } else if (type === 'return' || type === 'debit_note') {
                        val = -amount;
                    }
                }
            }

            if (val > 0) gross += val;

            total += val;

            const tDate = new Date(t.transaction_date);
            if (tDate >= startOfMonth) monthly += val;
            if (tDate >= startOfWeek) weekly += val;
        });

        setStats({
            totalPurchases: total,
            grossPurchases: gross,
            monthlyPurchases: monthly,
            weeklyPurchases: weekly,
            totalPayments: payments,
            transactionCount: data.length
        });
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(val);
    };

    const formatCurrencyWithoutSymbol = (amount) => {
        if (!amount) return '0.00';
        const num = parseFloat(amount || 0);
        return num.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDateDDMMYYYY = (date) => {
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    const exportToPDF = () => {
        if (!filteredTransactions.length) {
            alert('No transactions to export!');
            return;
        }

        try {
            const doc = new jsPDF('landscape');

            // ----------HEADER----------
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 128); // Navy Blue
            doc.text('OMEGA', 14, 20);

            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0); // Black
            doc.text('Purchase Executive Transaction Report (Raw Materials)', 14, 30);

            doc.setDrawColor(0, 0, 128);
            doc.line(14, 35, 280, 35); // Horizontal line

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Executive:', 14, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(officerName || 'N/A', 35, 45);

            doc.setFont('helvetica', 'bold');
            doc.text('Report Date:', 200, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(formatDateDDMMYYYY(new Date()), 225, 45);

            let yPos = 55;
            if (fromDate || toDate) {
                doc.setFont('helvetica', 'bold');
                doc.text('Period:', 14, yPos);
                doc.setFont('helvetica', 'normal');
                doc.text(`${fromDate || 'Start'} to ${toDate || 'End'}`, 30, yPos);
                yPos += 10;
            }

            // ---------- TABLE DATA ----------
            const tableData = [];
            filteredTransactions.forEach(t => {
                if (t.items && t.items.length > 0) {
                    // Filter items if product filter is present
                    const displayItems = productFilter
                        ? t.items.filter(item => item.product?.toLowerCase().includes(productFilter.toLowerCase()))
                        : t.items;

                    displayItems.forEach(item => {
                        const q = parseFloat(item.quantity) || 0;
                        const p = parseFloat(item.unit_price) || 0;
                        const total = parseFloat(item.total_amount) || (q * p);

                        tableData.push([
                            formatDateDDMMYYYY(t.transaction_date),
                            t.client_name || '-',
                            item.product || '-',
                            q,
                            formatCurrencyWithoutSymbol(p),
                            formatCurrencyWithoutSymbol(total)
                        ]);
                    });
                } else {
                    const type = (t.transaction_type || '').toLowerCase();
                    const q = parseFloat(t.quantity) || 0;
                    const p = parseFloat(t.unit_price) || parseFloat(t.price) || 0;
                    const totalVal = type === 'payment' ? t.amount_paid : (parseFloat(t.total_amount) || (q * p));

                    tableData.push([
                        formatDateDDMMYYYY(t.transaction_date),
                        t.client_name || '-',
                        t.product || '-',
                        q,
                        type === 'payment' ? '-' : formatCurrencyWithoutSymbol(p),
                        formatCurrencyWithoutSymbol(totalVal)
                    ]);
                }
            });

            autoTable(doc, {
                startY: yPos + 5,
                head: [
                    ['Date', 'Vendor', 'Raw Material', 'Qty', 'Unit Price', 'Total']
                ],
                body: tableData,
                styles: { fontSize: 9, cellPadding: 4 },
                headStyles: { fillColor: [0, 0, 128], textColor: 255 },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 50 },
                    2: { cellWidth: 50 },
                    3: { cellWidth: 20, halign: 'center' },
                    4: { cellWidth: 30, halign: 'right' },
                    5: { cellWidth: 30, halign: 'right' },
                    6: { cellWidth: 30, halign: 'center' }
                },
                margin: { left: 14, right: 14 }
            });

            // ---------- SUMMARY ----------
            const finalY = doc.lastAutoTable.finalY + 10;

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text('Summary:', 14, finalY);

            doc.text(`Total Transactions: ${stats.transactionCount}`, 14, finalY + 6);
            doc.text(`Total Purchase Amount: ${formatCurrencyWithoutSymbol(stats.totalPurchases)}`, 80, finalY + 6);

            // ---------- FOOTER ----------
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.text(
                'End of Report • Generated by Omega Purchase System',
                14,
                finalY + 20
            );

            const dateStr = formatDateDDMMYYYY(new Date());
            const sanitizedName = (officerName || 'Executive').replace(/[^a-zA-Z0-9]/g, '_');
            doc.save(`Purchase_Report_${sanitizedName}_${dateStr}.pdf`);

        } catch (error) {
            // console.error('PDF Error:', error);
            alert('Failed to generate PDF');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    if (loading) return (
        <Container className="text-center mt-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading summary...</p>
        </Container>
    );

    if (error) return (
        <Container className="mt-5">
            <Alert variant="danger">{error}</Alert>
            <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
        </Container>
    );

    return (
        <Container fluid className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button variant="outline-secondary" size="sm" className="mb-2" onClick={() => navigate(-1)}>
                        &larr; Back
                    </Button>
                    <h2>Purchase Summary: {officerName}</h2>
                    <p className="text-muted">Performance Overview</p>
                </div>
                <div className="d-flex align-items-center">
                    <Button variant="outline-primary" onClick={fetchPurchaseData} className="me-2">
                        <i className="bi bi-arrow-clockwise"></i> Refresh
                    </Button>
                    {/* Export button moved to filter section or keep here? User wants same as Sales. 
                        In Sales we have Refresh + Export top right. I'll put it in filter section to match recent Sales layout or keep it here if that's what user prefers. 
                        Wait, in SalesExecutiveSummary.js read above, Export is Top Right AND inside filter? No, user just said "export button and filter". 
                        Actually in SalesExecutiveSummary.js read earlier, it has Export button in the top right AND clear button in filters.
                        Let's check the view_file of SalesExecutiveSummary.js again.
                        Line 228: Refresh and Export are in the top right div. 
                        Line 296: Clear Filters button is in the filter card.
                        Okay, I will match that exact layout.
                    */}
                    <Button variant="success" onClick={exportToPDF} disabled={filteredTransactions.length === 0}>
                        <i className="bi bi-file-earmark-pdf"></i> Export PDF
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            <Row className="mb-4 gy-4">
                <Col md={4}>
                    <Card className="text-white mb-3 shadow-sm h-100 border-0" style={{ background: 'linear-gradient(45deg, #007bff, #6610f2)' }}>
                        <Card.Body>
                            <Card.Title className="opacity-75">Weekly Performance</Card.Title>
                            <h3 className="fw-bold">{formatCurrency(stats.weeklyPurchases)}</h3>
                            <Card.Text className="small opacity-75">Net (Current Week)</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-white mb-3 shadow-sm h-100 border-0" style={{ background: 'linear-gradient(45deg, #28a745, #20c997)' }}>
                        <Card.Body>
                            <Card.Title className="opacity-75">Monthly Performance</Card.Title>
                            <h3 className="fw-bold">{formatCurrency(stats.monthlyPurchases)}</h3>
                            <Card.Text className="small opacity-75">Net (Current Month)</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-white mb-3 shadow-sm h-100 border-0" style={{ background: 'linear-gradient(45deg, #b88017ff, #b34e0bff)' }}>
                        <Card.Body>
                            <Card.Title className="opacity-75">Gross Purchases</Card.Title>
                            <h3 className="fw-bold">{formatCurrency(stats.grossPurchases)}</h3>
                            <Card.Text className="small opacity-75">Without Returns</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-white bg-danger mb-3 shadow-sm h-100 border-0" style={{ background: 'linear-gradient(45deg, #dc3545, #b02a37)' }}>
                        <Card.Body>
                            <Card.Title className="opacity-75">Payments Released</Card.Title>
                            <h3 className="fw-bold">{formatCurrency(stats.totalPayments)}</h3>
                            <Card.Text className="small opacity-75">Total Paid to Vendors</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-white bg-primary mb-3 shadow-sm h-100 border-0" style={{ background: 'linear-gradient(45deg, #2c3e50, #34495e)' }}>
                        <Card.Body>
                            <Card.Title className="opacity-75">Net Performance</Card.Title>
                            <h3 className="fw-bold">{formatCurrency(stats.totalPurchases)}</h3>
                            <Card.Text className="small opacity-75">Gross - Returns</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Filters matching SalesExecutiveSummary */}
            <Card className="mb-4 shadow-sm border-0 bg-white">
                <Card.Body className="py-3">
                    <Row className="align-items-center">
                        <Col md={4} className="mb-2 mb-md-0">
                            <Form.Group controlId="fromDate">
                                <Form.Label className="mb-1 small text-muted fw-bold">FROM DATE</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="shadow-sm border-light"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4} className="mb-2 mb-md-0">
                            <Form.Group controlId="toDate">
                                <Form.Label className="mb-1 small text-muted fw-bold">TO DATE</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="shadow-sm border-light"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4} className="mb-2 mb-md-0">
                            <Form.Group controlId="vendorFilter">
                                <Form.Label className="mb-1 small text-muted fw-bold">VENDOR</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Search vendor..."
                                    value={vendorFilter}
                                    onChange={(e) => setVendorFilter(e.target.value)}
                                    className="shadow-sm border-light"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="align-items-center mt-3">
                        <Col md={4} className="mb-2 mb-md-0">
                            <Form.Group controlId="productFilter">
                                <Form.Label className="mb-1 small text-muted fw-bold">RAW MATERIAL</Form.Label>
                                <Form.Select
                                    value={productFilter}
                                    onChange={(e) => setProductFilter(e.target.value)}
                                    className="shadow-sm border-light"
                                >
                                    <option value="">All Raw Materials</option>
                                    {rawMaterials.map(m => (
                                        <option key={m.id} value={m.material_name || m.name}>{m.material_name || m.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                            <div className="d-flex gap-3 mb-1">
                                <Form.Check
                                    type="checkbox"
                                    id="showPayments"
                                    label="Payments"
                                    checked={showPayments}
                                    onChange={(e) => setShowPayments(e.target.checked)}
                                    className="fw-bold text-muted small"
                                />
                                <Form.Check
                                    type="checkbox"
                                    id="showOpeningBalance"
                                    label="Opening Balance"
                                    checked={showOpeningBalance}
                                    onChange={(e) => setShowOpeningBalance(e.target.checked)}
                                    className="fw-bold text-muted small"
                                />
                            </div>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                            <Button
                                variant="outline-secondary"
                                className="w-100 mt-2 shadow-sm"
                                onClick={() => {
                                    setFromDate('');
                                    setToDate('');
                                    setVendorFilter('');
                                    setProductFilter('');
                                    setShowPayments(true);
                                    setShowOpeningBalance(true);
                                }}
                            >
                                <i className="bi bi-x-circle"></i> Clear Filters
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Recent Transactions */}
            <Card className="shadow-sm border-0">
                <Card.Header className="bg-white py-3 border-bottom border-light">
                    <h5 className="mb-0 text-primary fw-bold">Recent Transactions</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0 text-nowrap align-middle">
                        <thead className="bg-light text-muted">
                            <tr style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                <th className="ps-4">Date</th>
                                <th>Vendor</th>
                                <th>Raw Material</th>
                                <th className="text-center">Qty</th>
                                <th className="text-end">Price</th>
                                <th className="text-end pe-4">Total</th>
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.95rem' }}>
                            {filteredTransactions.length > 0 ? (
                                filteredTransactions.map((t) => {
                                    const hasItems = t.items && t.items.length > 0;

                                    if (hasItems) {
                                        const displayItems = productFilter
                                            ? t.items.filter(item => item.product?.toLowerCase().includes(productFilter.toLowerCase()))
                                            : t.items;

                                        return displayItems.map((item, idx) => (
                                            <tr key={`${t.id}-${idx}`}>
                                                {idx === 0 ? (
                                                    <>
                                                        <td rowSpan={displayItems.length} className="ps-4 text-muted fw-medium">{formatDate(t.transaction_date)}</td>
                                                        <td rowSpan={displayItems.length}>
                                                            <span className="fw-bold text-dark">{t.client_name}</span>
                                                        </td>
                                                    </>
                                                ) : null}
                                                <td className="text-secondary">{item.product}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark border">{item.quantity}</span>
                                                </td>
                                                <td className="text-end text-muted">{formatCurrency(item.unit_price)}</td>
                                                <td className="text-end pe-4">
                                                    <span className="fw-bold text-success">{formatCurrency(item.total_amount)}</span>
                                                </td>
                                            </tr>
                                        ));
                                    } else {
                                        const type = (t.transaction_type || '').toLowerCase();
                                        const totalDisplay = type === 'payment' ? t.amount_paid : (t.total_amount || (t.quantity * (t.unit_price || t.price || 0)));

                                        return (
                                            <tr key={t.id}>
                                                <td className="ps-4 text-muted fw-medium">{formatDate(t.transaction_date)}</td>
                                                <td>
                                                    <span className="fw-bold text-dark">{t.client_name}</span>
                                                </td>
                                                <td className="text-secondary">{t.product}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark border">{t.quantity}</span>
                                                </td>
                                                <td className="text-end text-muted">
                                                    {type === 'payment' ? '-' : formatCurrency(t.unit_price || t.price || 0)}
                                                </td>
                                                <td className="text-end pe-4">
                                                    <span className={`fw-bold ${type === 'payment' ? 'text-warning' : 'text-success'}`}>
                                                        {formatCurrency(totalDisplay)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    }
                                })
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5">
                                        <div className="text-muted">
                                            <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                                            No transactions found for the selected period
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default PurchaseExecutiveSummary;
